import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import seedrandom from "seedrandom";
import { Rating } from "./react-simple-star-rating/src";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import debounce from "lodash/debounce";
import { FaInfo } from "react-icons/fa";
import "./Modal.css";

const defaultRating = 3;

function QuestionSelector({
  questions,
  currentQuestionId,
  onQuestionChange,
  isAdmin,
  onNewQuestion,
}) {
  return (
    <div className="question-selector">
      <select
        value={currentQuestionId}
        onChange={(e) => onQuestionChange(e.target.value)}
      >
        {Array.from(questions.entries()).map(([id, question]) => (
          <option key={id} value={id}>
            {question.id} {question.isPoll ? "(Poll)" : ""}
          </option>
        ))}
      </select>
      {isAdmin && <button onClick={onNewQuestion}>New Question/Poll</button>}
    </div>
  );
}

function NewQuestionModal({ onSubmit, onClose }) {
  const [questionText, setQuestionText] = useState("");
  const [isPoll, setIsPoll] = useState(false);
  const [pollOptions, setPollOptions] = useState("");
  const [includeOther, setIncludeOther] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    let options = isPoll
      ? pollOptions.split("\n").filter((option) => option.trim() !== "")
      : [];
    if (isPoll && includeOther) {
      options.push("Other");
    }
    onSubmit({ questionText, isPoll, pollOptions: options });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Create New Question/Poll</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="questionText">Question/Poll Text:</label>
            <input
              id="questionText"
              type="text"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Enter question or poll text, e.g. What should we think about next?"
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isPoll}
                onChange={(e) => setIsPoll(e.target.checked)}
              />
              Is this a poll?
            </label>
          </div>
          {isPoll && (
            <>
              <div className="form-group">
                <label htmlFor="pollOptions">Poll Options:</label>
                <textarea
                  id="pollOptions"
                  value={pollOptions}
                  onChange={(e) => setPollOptions(e.target.value)}
                  placeholder="Enter poll options (one per line)"
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={includeOther}
                    onChange={(e) => setIncludeOther(e.target.checked)}
                  />
                  Include "Other" option
                </label>
              </div>
            </>
          )}
          <div className="modal-buttons">
            <button
              type="submit"
              title="Creating a new question brings everyone to a new screen. You can go back by selecting the previous question from the dropdown."
            >
              Create
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PollComponent({ question, onVote, username }) {
  // convert pollvotes from array of entries to map.
  const pollVotes = new Map(question.pollVotes);
  const userVote = pollVotes.get(username);

  const renderPollResults = () => {
    const totalVotes = pollVotes.size;
    const optionCounts = {};
    pollVotes.values().forEach((option) => {
      optionCounts[option] = (optionCounts[option] || 0) + 1;
    });
    console.log("pollVotes", pollVotes);
    console.log("optionCounts", optionCounts);
    console.log("pollOptions", question.pollOptions);
    console.log("totalVotes", totalVotes);

    return (
      <div className="poll-results">
        {question.pollOptions.map((option) => {
          const voteCount = optionCounts[option] || 0;
          const percentage =
            totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
          const isUserVote = option === userVote;

          return (
            <div key={option} className="poll-option">
              <div className="poll-option-text">{option}</div>
              <div className="poll-option-bar-container">
                <div
                  className={`poll-option-bar ${isUserVote ? "user-vote" : ""}`}
                  style={{ width: `${percentage}%` }}
                ></div>
                {/* <span className="poll-option-percentage">
                  {voteCount}%
                </span> */}
              </div>
              <div className="poll-option-votes">{voteCount}</div>
            </div>
          );
        })}
        <div className="poll-total-votes">{totalVotes} total votes</div>
      </div>
    );
  };

  return (
    <div className="poll-component">
      {!userVote ? (
        <>
          {question.pollOptions.map((option) => (
            <React.Fragment key={option}>
              <button
                onClick={() => onVote(option)}
                className="poll-option-button"
              >
                {option}
              </button>
              <br /> {/* Add line break after each button */}
            </React.Fragment>
          ))}
        </>
      ) : (
        <>
          <p>You voted: {userVote}</p>
          {renderPollResults()}
        </>
      )}
    </div>
  );
}

function SentimentBar({
  userSentiment,
  allSentiments,
  onSentimentChange,
  onReset,
  isAdmin,
}) {
  const totalUsers = allSentiments.length;
  const interestedUsers = allSentiments.filter(
    ([, sentiment]) => sentiment === "interested"
  ).length;
  const boredUsers = allSentiments.filter(
    ([, sentiment]) => sentiment === "bored"
  ).length;
  const neutralUsers = totalUsers - interestedUsers - boredUsers;

  const interestedPercentage = (interestedUsers / totalUsers) * 100 || 0;
  const boredPercentage = (boredUsers / totalUsers) * 100 || 0;
  const neutralPercentage = (neutralUsers / totalUsers) * 100 || 0;

  return (
    <div className="sentiment-bar">
      <div className="sentiment-controls">
        Live sentiment:{" "}
        <button
          className={`sentiment-button ${userSentiment === "bored" ? "active" : ""
            }`}
          onClick={() => onSentimentChange("bored")}
          title="Let's move on"
        >
          🥱
        </button>
        <div className="sentiment-progress">
          <div
            className="sentiment-progress-bored"
            style={{ width: `${boredPercentage}%` }}
          ></div>
          <div
            className="sentiment-progress-neutral"
            style={{ width: `${neutralPercentage}%` }}
          ></div>
          <div
            className="sentiment-progress-interested"
            style={{ width: `${interestedPercentage}%` }}
          ></div>
        </div>
        <button
          className={`sentiment-button ${userSentiment === "interested" ? "active" : ""
            }`}
          onClick={() => onSentimentChange("interested")}
          title="Continue with this topic"
        >
          🤩
        </button>
        {isAdmin && (
          <button className="reset-sentiment-button" onClick={onReset}>
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

function Idea({
  idea,
  rating,
  onRatingChange,
  onCommentSubmit,
  onDelete,
  isAdmin,
  ucbScore,
  showBackground,
  ratingMechanism,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const getBackgroundColor = () => {
    if (!showBackground) return "white";
    const hue = 120; // Green hue
    const saturation = 100;
    const lightness = 100 - ucbScore ** 2 * 10; // Ranges from 90% to 100% lightness
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const renderRating = () => {
    if (ratingMechanism === "fivestar") {
      return (
        <Rating
          onClick={(value) => {
            onRatingChange(idea.id, value);
          }}
          initialValue={rating || defaultRating}
          size={20}
          fillColor={rating === 0 ? "#b0b0b0" : "#ffc107"}
        />
      );
    } else {
      const upvoted = rating === 1;
      const downvoted = rating === -1;
      return (
        <div className="updownvote-container">
          <button
            className={`vote-button upvote ${upvoted ? "active" : ""}`}
            onClick={() => onRatingChange(idea.id, upvoted ? 0 : 1)}
            title="Upvote"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M12 4 L4 20 L20 20 Z" />
            </svg>
          </button>
          <button
            className={`vote-button downvote ${downvoted ? "active" : ""}`}
            onClick={() => onRatingChange(idea.id, downvoted ? 0 : -1)}
            title="Downvote"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M4 4 L20 4 L12 20 Z" />
            </svg>
          </button>
        </div>
      );
    }
  };

  return (
    <React.Fragment>
      <tr
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ backgroundColor: getBackgroundColor() }}
      >
        <td className="idea-column">
          {rating === 0 ? <strong>{idea.name}</strong> : idea.name}
          {isAdmin && isHovered && (
            <button
              onClick={() => onDelete(idea.id)}
              className="delete-idea-button"
            >
              X
            </button>
          )}
        </td>
        <td>{renderRating()}</td>
        <td>
          <button onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? "Hide" : "Show"} ({idea.comments.length})
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan="3" className="comments">
            <ul>
              {idea.comments.map((comment, index) => (
                <li key={index}>
                  {comment.username}: <em>{comment.comment}</em>{" "}
                </li>
              ))}
            </ul>
            <input
              type="text"
              placeholder="Add a comment"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onCommentSubmit(idea.id, e.target.value);
                  e.target.value = "";
                }
              }}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function RoomCreation({ colorMode, setColorMode }) {
  const [topic, setTopic] = useState(
    "What questions, ideas, or suggestions do you have?"
  );
  const [aiGeneration, setAiGeneration] = useState(false);
  const [showVoteMap, setShowVoteMap] = useState(false);
  const [showVotingStats, setShowVotingStats] = useState(false);
  const [showClusterLeaderboards, setShowClusterLeaderboards] = useState(false);
  const [showLiveSentiment, setShowLiveSentiment] = useState(false);
  const [ratingMechanism, setRatingMechanism] = useState("updown");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const response = await fetch("/api/create-room", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        topic,
        aiGeneration,
        showVoteMap,
        showVotingStats,
        showClusterLeaderboards,
        showLiveSentiment,
        ratingMechanism,
      }),
    });
    const data = await response.json();
    navigate(`/room/${data.roomId}?admin=${data.adminToken}`);
  };

  return (
    <div className="room-creation">
      <div className="top-section">
        <div className="top-section-content">
          <ColorModeToggle colorMode={colorMode} setColorMode={setColorMode} />
        </div>
      </div>
      <h2>Create Room</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="topic">Topic:</label>
          <input
            type="text"
            id="topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Enter room topic"
          />
        </div>

        {/* <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={aiGeneration}
              onChange={(e) => setAiGeneration(e.target.checked)}
            />
            Enable AI idea generation
          </label>
        </div> */}

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={showVotingStats}
              onChange={(e) => setShowVotingStats(e.target.checked)}
            />
            Show Voting Stats (to non-admin users).
          </label>
        </div>

        {/* <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={showLiveSentiment}
              onChange={(e) => setShowLiveSentiment(e.target.checked)}
            />
            Enable Live Sentiment
          </label>
        </div> */}

        <div className="form-group">
          <label>Rating Mechanism:</label>
          <select
            value={ratingMechanism}
            onChange={(e) => setRatingMechanism(e.target.value)}
          >
            <option value="updownvote">Upvote/Downvote</option>
            <option value="fivestar">5-Star Rating</option>
          </select>
        </div>

        <div className="form-group">
          <button type="submit">Create Room</button>
        </div>
      </form>
    </div>
  );
}

// Add this function outside of the Room component
function calculateVotingStats(data, currentUsername, ratingMechanism) {
  const stats = {};
  data.forEach((metadata) => {
    // Count idea submissions
    const provenance = metadata.provenance || "Unknown";
    stats[provenance] = stats[provenance] || { ideas: 0, ratings: 0, upvotes: 0, downvotes: 0 };
    stats[provenance].ideas++;

    // Count ratings
    Object.entries(metadata.ratings || {}).forEach(([rater, rating]) => {
      stats[rater] = stats[rater] || { ideas: 0, ratings: 0, upvotes: 0, downvotes: 0 };
      stats[rater].ratings++;

      // Count upvotes and downvotes for upvote/downvote mode
      if (ratingMechanism !== "fivestar") {
        if (rating === 1) {
          stats[rater].upvotes++;
        } else if (rating === -1) {
          stats[rater].downvotes++;
        }
      }
    });
  });

  return Object.entries(stats)
    .map(([user, userStats]) => ({
      user,
      ideas: userStats.ideas,
      ratings: userStats.ratings,
      upvotes: userStats.upvotes,
      downvotes: userStats.downvotes,
      isCurrentUser: user === currentUsername,
    }))
    .sort((a, b) => b.ratings - a.ratings); // Sort by ratings in reverse order
}

function calculateLeaderboardStats(ratings, totalUsers, ratingMechanism) {
  if (ratingMechanism === "fivestar") {
    const validRatings = ratings.filter((rating) => rating > 0);
    const numVotes = validRatings.length;
    const sumRatings = validRatings.reduce((sum, rating) => sum + rating, 0);
    // Assume non-voters have a rating of 3, but only up to 5 non-voters.
    // this functions as a prior.
    const numNonVotes = Math.min(totalUsers - numVotes, 5);
    const sumNonVotes = numNonVotes * 3;
    const avgRating = (sumRatings + sumNonVotes) / (numVotes + numNonVotes);
    const avgVotes = numVotes > 0 ? sumRatings / numVotes : 0;
    const stdDev =
      numVotes > 1
        ? Math.sqrt(
          validRatings.reduce(
            (sum, rating) => sum + Math.pow(rating - avgVotes, 2),
            0
          ) /
          (numVotes - 1)
        )
        : 0;
    return { avgRating, stdDev, numVotes };
  } else {
    const validVotes = ratings.filter((rating) => rating !== 0);
    const numVotes = validVotes.length;
    // Assume non-voters have a rating of 0, but only up to 5 non-voters.
    // this functions as a prior.
    const numExtraVotes = Math.min(totalUsers - numVotes, 5);
    const numUpvotes = validVotes.filter((rating) => rating === 1).length;
    const fractionAgreeing =
      numVotes > 0 ? numUpvotes / (numVotes + numExtraVotes) : 0;
    return { fractionAgreeing, numVotes };
  }
}

function calculateLeaderboard(
  ideas,
  numUsers,
  userSubset = null,
  ratingMechanism
) {
  if (ideas.length === 0) {
    return [];
  }

  const totalUsers = userSubset ? userSubset.length : numUsers;
  return ideas
    .map((idea) => {
      const ratings = userSubset
        ? userSubset.map((userId) => idea.ratings[userId] || 0)
        : Object.values(idea.ratings);
      const { avgRating, stdDev, numVotes, fractionAgreeing } =
        calculateLeaderboardStats(ratings, totalUsers, ratingMechanism);
      return {
        name: idea.name,
        id: idea.id,
        avgRating,
        stdDev,
        numVotes,
        fractionAgreeing,
      };
    })
    .sort(
      (a, b) =>
        (b.avgRating || b.fractionAgreeing) -
        (a.avgRating || a.fractionAgreeing)
    );
}

function calculateLeaderboards(
  ideas,
  numUsers,
  subset1,
  subset2,
  ratingMechanism
) {
  return {
    leaderboard1: calculateLeaderboard(
      ideas,
      numUsers,
      subset1,
      ratingMechanism
    ),
    leaderboard2: calculateLeaderboard(
      ideas,
      numUsers,
      subset2,
      ratingMechanism
    ),
  };
}

function Room({ colorMode, setColorMode }) {
  const { roomId } = useParams();
  const location = useLocation();
  const [username, setUsername] = useState(
    () => sessionStorage.getItem("username") || ""
  );
  const [hasJoined, setHasJoined] = useState(
    () => !!sessionStorage.getItem("username")
  );
  const [roomInfo, setRoomInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminToken, setAdminToken] = useState("");
  const [context, setContext] = useState("");
  const [questions, setQuestions] = useState(new Map());
  const [lastFocusPage, setLastFocusPage] = useState(null);
  const [lastLocalFocusPage, setLastLocalFocusPage] = useState(null);
  const [newIdea, setNewIdea] = useState("");
  const [ws, setWs] = useState(null);
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [task, setTask] = useState("");
  const [votingStats, setVotingStats] = useState([]);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [joinError, setJoinError] = useState("");
  const debouncedCheckUsername = useRef(null);
  const [showVoteMap, setShowVoteMap] = useState(false);
  const [showVotingStats, setShowVotingStats] = useState(false);
  const [showClusterLeaderboards, setShowClusterLeaderboards] = useState(false);
  const unratedIdeasRef = useRef(null);
  const [userSentiment, setUserSentiment] = useState("");
  const [allSentiments, setAllSentiments] = useState([]);
  const [isDisconnected, setIsDisconnected] = useState(false);
  const [showNewQuestionModal, setShowNewQuestionModal] = useState(false);
  const [userVotes, setUserVotes] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ratingMechanism, setRatingMechanism] = useState("fivestar");
  const [rateLimitMessage, setRateLimitMessage] = useState("");
  // Per-question moderation queues (admin-only). Map<questionId, idea[]>.
  const [moderationQueues, setModerationQueues] = useState(new Map());
  const [showModerationInfo, setShowModerationInfo] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const adminTokenFromUrl = searchParams.get("admin");
    setAdminToken(adminTokenFromUrl || "");

    fetch(
      `/api/room-info/${roomId}${adminTokenFromUrl ? `?admin=${adminTokenFromUrl}` : ""
      }`
    )
      .then((response) => response.json())
      .then((data) => {
        setRoomInfo(data);
        setTask(data.topic);
        setIsAdmin(data.isAdmin);
        setShowVoteMap(data.showVoteMap);
        setShowVotingStats(data.showVotingStats);
        setShowClusterLeaderboards(data.showClusterLeaderboards);
        setRatingMechanism(data.ratingMechanism);
      })
      .catch((error) => console.error("Error fetching room info:", error));
  }, [roomId, location.search]);

  const currentQuestionId = useMemo(() => {
    // Take the last focused page or last local focused page, whichever is more recent by timestamp.
    if (lastFocusPage && lastLocalFocusPage) {
      return lastFocusPage.timestamp > lastLocalFocusPage.timestamp
        ? lastFocusPage.questionId
        : lastLocalFocusPage.questionId;
    } else if (lastFocusPage) {
      return lastFocusPage.questionId;
    } else if (lastLocalFocusPage) {
      return lastLocalFocusPage.questionId;
    } else {
      return "";
    }
  }, [lastFocusPage, lastLocalFocusPage]);

  const currentQuestion = useMemo(() => {
    return questions.get(currentQuestionId);
  }, [questions, currentQuestionId]);

  const rawIdeas = useMemo(() => {
    return currentQuestion?.ideas || [];
  }, [currentQuestion]);

  // Responses held for moderation on the current question (admins only ever
  // receive these from the server).
  const moderationItems = useMemo(() => {
    return moderationQueues.get(currentQuestionId) || [];
  }, [moderationQueues, currentQuestionId]);

  // Compute unselected items.
  const unselectedIdeas = useMemo(() => {
    return rawIdeas.filter((idea) => idea.selected === 0);
  }, [rawIdeas]);
  const selectedIdeas = useMemo(() => {
    return rawIdeas
      .filter((idea) => idea.selected !== 0)
      .sort((a, b) => b.selected - a.selected);
  }, [rawIdeas]);

  // Compute total number of users
  const totalUsers = useMemo(() => {
    const allUsers = new Set();
    unselectedIdeas.forEach((idea) => {
      Object.keys(idea.ratings).forEach((user) => allUsers.add(user));
    });
    return allUsers.size;
  }, [unselectedIdeas]);

  // Add this new function to calculate the overall leaderboard
  const calculateOverallLeaderboard = useMemo(() => {
    return calculateLeaderboard(
      unselectedIdeas,
      totalUsers,
      null,
      ratingMechanism
    );
  }, [unselectedIdeas, totalUsers, ratingMechanism]);

  useEffect(() => {
    debouncedCheckUsername.current = debounce(async (username) => {
      if (username.trim() && roomInfo) {
        try {
          const response = await fetch(
            `/api/check-username/${roomInfo.roomId}/${username}`
          );
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const contentType = response.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Oops! Received non-JSON response from the server");
          }
          const data = await response.json();
          setUsernameAvailable(data.isAvailable);
          setJoinError("");
        } catch (error) {
          console.error("Error checking username:", error);
          setUsernameAvailable(null);
          setJoinError(
            "Error checking username availability. Please try again."
          );
        }
      } else {
        setUsernameAvailable(null);
        setJoinError("");
      }
    }, 300);
  }, [roomInfo]);

  useEffect(() => {
    if (debouncedCheckUsername.current) {
      debouncedCheckUsername.current(username);
    }
  }, [username]);

  useEffect(() => {
    if (!roomInfo || !hasJoined) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(
      `${protocol}//${window.location.host}/ws/${roomInfo.roomId}`
    );
    setWs(socket);

    socket.onopen = () => {
      socket.send(
        JSON.stringify({
          type: "join",
          username,
          // Sent so the server can mark this connection as admin and stream it
          // the moderation queue. Non-admins simply omit/mismatch this token.
          adminToken,
        })
      );
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "joinError") {
        setJoinError(data.message);
        setHasJoined(false);
        sessionStorage.removeItem("username");
      } else if (data.type === "roomUpdate") {
        setQuestions(function (questions) {
          const newQuestions = new Map(questions);
          data.data.questions.forEach((q) => {
            newQuestions.set(q.id, {
              ...q,
              ideas: q.ideas.map(([id, metadata]) => ({
                id,
                ...metadata,
              })),
            });
          });
          return newQuestions;
        });

        setLastFocusPage(data.data.lastFocusPage);
      } else if (data.type === "newQuestion") {
        setLastFocusPage({
          questionId: data.questionId,
          timestamp: Date.now(),
        });
      } else if (data.type === "moderationUpdate") {
        setModerationQueues((prev) => {
          const next = new Map(prev);
          next.set(
            data.data.questionId,
            data.data.queue.map(([id, metadata]) => ({ id, ...metadata }))
          );
          return next;
        });
      } else if (data.type === "rateLimited") {
        // Our optimistic "Submitted!" was wrong — the server throttled us.
        setIsSubmitted(false);
        setRateLimitMessage(data.message);
      } else if (data.type === "generatingIdeas") {
        setIsGeneratingIdeas(data.isGenerating);
      } else if (data.type === "sentimentUpdate") {
        setAllSentiments(data.data);
        // Update the current user's sentiment
        const userSentimentData = data.data.find(([user]) => user === username);
        if (userSentimentData) {
          setUserSentiment(userSentimentData[1]);
        }
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    socket.onclose = (event) => {
      console.log(
        "WebSocket connection closed:",
        event,
        event.code,
        event.reason
      );
      setIsDisconnected(true);
    };
  }, [username, roomInfo, hasJoined]);

  const sendMessage = useCallback(
    (type, data) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type, ...data }));
      }
    },
    [ws]
  );

  const handleQuestionChange = (questionId) => {
    setLastLocalFocusPage({
      questionId,
      timestamp: Date.now(),
    });
  };

  const handleNewQuestion = (questionData) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({ type: "newQuestion", ...questionData, adminToken })
      );
    }
    setShowNewQuestionModal(false);
  };

  const handleVote = (questionId, option) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "vote", questionId, option, username }));
      currentQuestion.pollVotes[username] = option;
    }
  };

  const handleNewIdea = useCallback(() => {
    if (newIdea.trim() && newIdea.trim().length >= 20 && currentQuestionId) {
      const idea = newIdea.trim();
      const ideaId =
        Date.now().toString() + Math.random().toString(36).substr(2, 9);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            type: "newIdea",
            questionId: currentQuestionId,
            username,
            idea: { id: ideaId, name: idea.trim() },
          })
        );
      }
      setNewIdea("");
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 1000);
    }
  }, [newIdea, currentQuestionId, username, ws]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (newIdea.trim().length >= 20) {
        handleNewIdea();
      }
    }
  };

  const handleRatingChange = useCallback(
    (ideaId, rating) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            type: "newRating",
            questionId: currentQuestionId,
            username,
            ideaId,
            rating,
          })
        );
      }
    },
    [currentQuestionId, username, ws]
  );

  const handleCommentSubmit = useCallback(
    (id, comment) => {
      sendMessage("newComment", {
        username,
        ideaId: id,
        comment,
        questionId: currentQuestionId,
      });
    },
    [sendMessage, username, currentQuestionId]
  );

  const handleGenerateIdeas = useCallback(() => {
    if (!isGeneratingIdeas && roomInfo && roomInfo.aiGeneration) {
      sendMessage("generateIdeas", { task });
    }
  }, [sendMessage, isGeneratingIdeas, task, roomInfo]);

  useEffect(() => {
    sendMessage("updateContext", { username, context });
  }, [context, sendMessage, username]);

  const handleDeleteIdea = useCallback(
    (ideaId) => {
      if (isAdmin && ws && ws.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            type: "deleteIdea",
            ideaId,
            questionId: currentQuestionId,
            adminToken,
          })
        );
      }
    },
    [isAdmin, ws, currentQuestionId, adminToken]
  );

  const handleApproveModeration = useCallback(
    (ideaId) => {
      if (isAdmin && ws && ws.readyState === WebSocket.OPEN) {
        sendMessage("approveModeration", {
          questionId: currentQuestionId,
          ideaId,
          adminToken,
        });
      }
    },
    [isAdmin, ws, sendMessage, currentQuestionId, adminToken]
  );

  const handleRejectModeration = useCallback(
    (ideaId) => {
      if (isAdmin && ws && ws.readyState === WebSocket.OPEN) {
        sendMessage("rejectModeration", {
          questionId: currentQuestionId,
          ideaId,
          adminToken,
        });
      }
    },
    [isAdmin, ws, sendMessage, currentQuestionId, adminToken]
  );

  useEffect(() => {
    if (!rateLimitMessage) return;
    const timer = setTimeout(() => setRateLimitMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [rateLimitMessage]);

  const handleBlockUser = useCallback(
    (blockedUsername) => {
      if (isAdmin && ws && ws.readyState === WebSocket.OPEN) {
        sendMessage("blockUser", {
          questionId: currentQuestionId,
          username: blockedUsername,
          adminToken,
        });
      }
    },
    [isAdmin, ws, sendMessage, currentQuestionId, adminToken]
  );

  const _ = useEffect(() => {
    setVotingStats(calculateVotingStats(rawIdeas, username, ratingMechanism));
  }, [rawIdeas, username, ratingMechanism]);

  const handleJoin = (e) => {
    e.preventDefault();
    if (username.trim() && usernameAvailable) {
      setHasJoined(true);
      sessionStorage.setItem("username", username);
      setJoinError("");
    }
  };

  const unratedIdeas = useMemo(() => {
    return rawIdeas.filter(
      (idea) => !idea.selected && idea.ratings[username] === undefined
    );
  }, [rawIdeas, username]);

  const ratedIdeas = useMemo(() => {
    return rawIdeas
      .filter((idea) => !idea.selected && idea.ratings[username] !== undefined)
      .sort((a, b) => b.ratings[username] - a.ratings[username]);
  }, [rawIdeas, username]);

  const calculateUCBScore = (idea) => {
    const ratings = Object.values(idea.ratings || {});
    const avgRating =
      ratings.length > 0
        ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length
        : 0;
    const maxExploration = 10;
    const explorationTerm = maxExploration / Math.sqrt(ratings.length || 1);
    const ucbScore = avgRating + explorationTerm;
    // Normalize to be between 0 and 1.
    const maxUCBScore = maxExploration + 5;
    const minUCBScore = 1;
    return (ucbScore - minUCBScore) / (maxUCBScore - minUCBScore);
  };

  const renderIdeasTable = (ideas, title, maxRows, ref = null) => {
    const ucbScores = ideas.map(calculateUCBScore);

    const sortedScoresWithIndices = ucbScores
      .map((score, index) => ({ score, index }))
      .sort((a, b) => a.score - b.score);

    let currentRank = 0;
    let previousScore = null;
    const normalizedUCBScores = new Array(ideas.length);

    sortedScoresWithIndices.forEach((item, i) => {
      if (item.score !== previousScore) {
        currentRank = i;
        previousScore = item.score;
      }
      normalizedUCBScores[item.index] = currentRank / ideas.length;
    });

    return (
      <div className="ideas-table-container">
        <h4>{title}</h4>
        <table className="ideas-list">
          <thead>
            <tr>
              <th className="idea-column">Idea</th>
              <th>Your Rating</th>
              <th>Comments</th>
            </tr>
          </thead>
        </table>
        <div
          className="ideas-table-scroll"
          ref={ref}
          style={{
            maxHeight: `${maxRows * 3}em`,
            overflowY: "auto",
          }}
        >
          <table className="ideas-list">
            <tbody>
              {ideas.map((idea, index) => (
                <Idea
                  key={idea.id}
                  idea={idea}
                  rating={idea.ratings[username] || 0}
                  onRatingChange={handleRatingChange}
                  onCommentSubmit={handleCommentSubmit}
                  onDelete={handleDeleteIdea}
                  isAdmin={isAdmin}
                  ucbScore={normalizedUCBScores[index]}
                  showBackground={title === "Responses to rate"}
                  ratingMechanism={ratingMechanism}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleSelectItem = useCallback(
    (ideaId) => {
      if (isAdmin) {
        sendMessage("selectItem", {
          ideaId,
          adminToken,
          questionId: currentQuestionId,
        });
      }
    },
    [
      currentQuestionId,
      isAdmin,
      sendMessage,
      adminToken,
      unselectedIdeas,
      totalUsers,
    ]
  );

  const renderLeaderboard = (leaderboard, title) => {
    const getBackgroundColor = (avgRating) => {
      if (ratingMechanism !== "fivestar") return "transparent";
      if (avgRating >= 3) return "transparent";
      const intensity = (3 - avgRating) / 3;
      return `rgba(255, 0, 0, ${0.1 + intensity * 0.3})`;
    };

    const tooltipText =
      ratingMechanism === "fivestar"
        ? "To downrank items with few votes, we add up to 5 fake neutral (3-star) votes."
        : "To downrank items with few votes, we add up to 5 fake neutral votes.";

    return (
      <div className="leaderboard-container">
        <h3>{title}</h3>
        <div className="leaderboard-scroll">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Idea</th>
                <th>
                  {ratingMechanism === "fivestar" ? "Avg Rating" : "Agreement"}
                  <span className="info-icon" title={tooltipText}>
                    ⓘ
                  </span>
                </th>
                {ratingMechanism === "fivestar" && <th>Controversy</th>}
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((idea, index) => (
                <tr
                  key={idea.id}
                  className={`${isAdmin ? "admin-clickable" : ""}`}
                  onClick={() => isAdmin && handleSelectItem(idea.id)}
                  style={{
                    backgroundColor: getBackgroundColor(
                      idea.avgRating || idea.fractionAgreeing
                    ),
                  }}
                >
                  <td>{idea.name}</td>
                  <td>
                    {ratingMechanism === "fivestar"
                      ? `${idea.avgRating.toFixed(2)} (${idea.numVotes} vote${idea.numVotes !== 1 ? "s" : ""
                      })`
                      : `${(idea.fractionAgreeing * 100).toFixed(1)}% (${idea.numVotes
                      } vote${idea.numVotes !== 1 ? "s" : ""})`}
                  </td>
                  {ratingMechanism === "fivestar" && (
                    <td>{idea.stdDev.toFixed(2)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSelectedItems = () => (
    <div className="selected-items">
      {isAdmin && (
        <p>
          (Admins) Click on an idea to mark it as "currently discussing" and
          archive it from the leaderboard.
        </p>
      )}
      {selectedIdeas.length > 0 && (
        <div>
          <h4>
            <em>Currently discussing:</em> {selectedIdeas[0].name}
          </h4>
          {selectedIdeas.length > 1 && (
            <details>
              <summary>Show history</summary>
              <ul>
                {selectedIdeas.slice(1).map((item, index) => (
                  <li key={index}>{item.name}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );

  const renderModerationQueue = () => {
    // Only shown to admins, and only when there is something to review.
    if (!isAdmin || moderationItems.length === 0) return null;
    const moderationHelpText =
      "(Admins only) These responses were flagged by the automated filter and are hidden from everyone until you approve them. Approve to publish to the responses and leaderboard, or reject to discard.";
    return (
      <div className="moderation-section">
        <div className="moderation-header">
          <h3>Flagged for moderation ({moderationItems.length})</h3>
          <button
            type="button"
            className="moderation-info-button"
            title={moderationHelpText}
            aria-label={moderationHelpText}
            aria-expanded={showModerationInfo}
            onClick={() => setShowModerationInfo((v) => !v)}
          >
            <FaInfo />
          </button>
        </div>
        {showModerationInfo && (
          <p className="moderation-help">{moderationHelpText}</p>
        )}
        <ul className="moderation-list">
          {moderationItems.map((item) => (
              <li key={item.id} className="moderation-item">
                <div className="moderation-item-body">
                  <div className="moderation-item-text">{item.name}</div>
                  <div className="moderation-item-meta">
                    From: {item.provenance}
                    {item.moderationReason ? ` — ${item.moderationReason}` : ""}
                  </div>
                </div>
                <div className="moderation-item-actions">
                  <div className="moderation-buttons">
                    <button
                      className="moderation-approve"
                      onClick={() => handleApproveModeration(item.id)}
                    >
                      Approve
                    </button>
                    <button
                      className="moderation-reject"
                      onClick={() => handleRejectModeration(item.id)}
                    >
                      Reject
                    </button>
                  </div>
                  <button
                    type="button"
                    className="moderation-block-link"
                    title="Block user from submitting additional responses"
                    onClick={() => handleBlockUser(item.provenance)}
                  >
                    Block user
                  </button>
                </div>
              </li>
            ))}
        </ul>
      </div>
    );
  };

  // Add this effect to maintain scroll position
  useEffect(() => {
    if (unratedIdeasRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = unratedIdeasRef.current;
      const distanceFromBottom = scrollHeight - clientHeight - scrollTop;

      // After the component updates
      requestAnimationFrame(() => {
        const newScrollHeight = unratedIdeasRef.current.scrollHeight;
        unratedIdeasRef.current.scrollTop =
          newScrollHeight - clientHeight - distanceFromBottom;
      });
    }
  }, [unratedIdeas]);

  const handleSentimentChange = (sentiment) => {
    const newSentiment = sentiment === userSentiment ? "" : sentiment;
    setUserSentiment(newSentiment);
    sendMessage("updateSentiment", { username, sentiment: newSentiment });
  };

  const handleResetSentiments = () => {
    sendMessage("resetSentiments", { adminToken });
  };

  const handleReconnect = () => {
    window.location.reload();
  };

  const getSubmitButtonText = () => {
    if (isSubmitted) return "Submitted! Thank you";
    const length = newIdea.trim().length;
    if (length === 0) return "Submit Response";
    if (length < 20) return "Response too short - please explain more";
    return "Submit Response";
  };

  return (
    <div className="App">
      {roomInfo ? (
        hasJoined ? (
          <>
            <div className="top-section">
              <div className="top-section-content">
                <ColorModeToggle
                  colorMode={colorMode}
                  setColorMode={setColorMode}
                />
                {isDisconnected && (
                  <div
                    onClick={handleReconnect}
                    className="disconnected-banner"
                  >
                    Disconnected. Click here to reload
                  </div>
                )}
                {!isDisconnected && (
                  <div className="username-display">
                    Joined as: {username} {isAdmin && "(Admin)"}
                  </div>
                )}
                <QuestionSelector
                  questions={questions}
                  currentQuestionId={currentQuestionId}
                  onQuestionChange={handleQuestionChange}
                  isAdmin={isAdmin}
                  onNewQuestion={() => setShowNewQuestionModal(true)}
                />
                {roomInfo.showLiveSentiment && (
                  <SentimentBar
                    userSentiment={userSentiment}
                    allSentiments={allSentiments}
                    onSentimentChange={handleSentimentChange}
                    onReset={handleResetSentiments}
                    isAdmin={isAdmin}
                  />
                )}
              </div>
            </div>
            <div className="scrollable-content">
              <div className="main-content">
                {showNewQuestionModal && (
                  <NewQuestionModal
                    onSubmit={handleNewQuestion}
                    onClose={() => setShowNewQuestionModal(false)}
                  />
                )}
                {currentQuestion && (
                  <>
                    <h2>{currentQuestionId}</h2>
                    {currentQuestion.isPoll ? (
                      <PollComponent
                        question={currentQuestion}
                        onVote={(option) =>
                          handleVote(currentQuestionId, option)
                        }
                        username={username}
                      />
                    ) : (
                      <>
                        <div className="new-idea-section">
                          <h3>1. Propose New Responses</h3>
                          <div className="new-idea">
                            <textarea
                              value={newIdea}
                              onChange={(e) => {
                                setNewIdea(e.target.value);
                                if (rateLimitMessage) setRateLimitMessage("");
                              }}
                              onKeyUp={handleKeyPress}
                              placeholder="Add a new response. Press Enter to submit"
                              rows={2}
                              maxLength={250}
                              className="new-idea-input"
                            />
                            <button
                              onClick={handleNewIdea}
                              className={`submit-idea-button ${isSubmitted ? "submitted" : ""
                                }`}
                              disabled={
                                newIdea.trim().length < 20 || isSubmitted
                              }
                            >
                              {getSubmitButtonText()}
                            </button>
                          </div>
                          {rateLimitMessage && (
                            <p className="rate-limit-notice">
                              {rateLimitMessage}
                            </p>
                          )}
                        </div>
                        {renderModerationQueue()}
                        <div className="rate-ideas-section">
                          <h3>2. Rate Responses</h3>
                          {ratingMechanism === "fivestar" && (
                            <p>
                              Rate by clicking on a star.
                              <br />1 star means you don't like it, 5 stars
                              means you love it, 3 stars means you don't care
                              <br />
                              Green background means your rating is especially
                              useful.
                            </p>
                          )}
                          <div className="ideas-tables">
                            {renderIdeasTable(
                              unratedIdeas,
                              "Responses to rate",
                              10,
                              unratedIdeasRef
                            )}
                            {renderIdeasTable(
                              ratedIdeas,
                              "Already rated responses",
                              3
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              {!currentQuestion?.isPoll && ( // Only show leaderboard for non-poll questions
                <div className="overall-leaderboard-section">
                  <h3>Leaderboard</h3>
                  {renderSelectedItems()}
                  {renderLeaderboard(calculateOverallLeaderboard, "")}
                </div>
              )}

              {showVoteMap && !currentQuestion?.isPoll && (
                <div className="vote-map-section">
                  <h2>Vote Map</h2>
                  <div id="ratings-plot"></div>
                </div>
              )}
              {(showVotingStats || isAdmin) && !currentQuestion?.isPoll && (
                <div className="voting-stats-section">
                  <h2>Voting Stats</h2>
                  <table className="voting-stats-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>New Responses</th>
                        <th>Ratings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {votingStats.map(
                        ({ user, ideas, ratings, upvotes, downvotes, isCurrentUser }) => (
                          <tr
                            key={user}
                            className={isCurrentUser ? "current-user" : ""}
                          >
                            <td>
                              {isCurrentUser ? <strong>{user}</strong> : user}
                            </td>
                            <td>
                              {isCurrentUser ? <strong>{ideas}</strong> : ideas}
                            </td>
                            <td>
                              {isCurrentUser ? (
                                <strong>
                                  {ratingMechanism === "fivestar"
                                    ? ratings
                                    : upvotes || downvotes
                                      ? `${ratings} (${upvotes} up/${downvotes} down)`
                                      : ratings}
                                </strong>
                              ) : (
                                ratingMechanism === "fivestar"
                                  ? ratings
                                  : upvotes || downvotes
                                    ? `${ratings} (${upvotes} up/${downvotes} down)`
                                    : ratings
                              )}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="join-form">
            <h2>Join Hivemind</h2>
            <form onSubmit={handleJoin}>
              <div className="username-input-container">
                <input
                  type="text"
                  name="usr"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your Discord username"
                  autocomplete="new-password"
                  required
                />
                {username.trim() && usernameAvailable !== null && (
                  <span
                    className={`username-availability ${usernameAvailable ? "available" : "unavailable"
                      }`}
                  >
                    {usernameAvailable ? "✓" : "✗"}
                  </span>
                )}
              </div>
              {joinError && <p className="join-error">{joinError}</p>}
              <button
                type="submit"
                disabled={!usernameAvailable || !username.trim()}
              >
                Join Room
              </button>
            </form>
          </div>
        )
      ) : (
        <div>Loading room information...</div>
      )}
    </div>
  );
}

function getInitialColorMode() {
  const savedMode = localStorage.getItem("colorMode");
  return savedMode || "system";
}

function ColorModeToggle({ colorMode, setColorMode }) {
  const handleToggle = () => {
    const newMode = colorMode === "light" ? "dark" : "light";
    setColorMode(newMode);
    localStorage.setItem("colorMode", newMode);
  };

  return (
    <button
      className="color-mode-toggle"
      onClick={handleToggle}
      title={`Switch to ${colorMode === "light" ? "dark" : "light"} mode`}
    >
      {colorMode === "light" ? "🌙" : "☀️"}
    </button>
  );
}

function App() {
  const [colorMode, setColorMode] = useState(getInitialColorMode);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light-mode", "dark-mode");

    if (colorMode === "system") {
      const systemPreference = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      root.classList.add(`${systemPreference}-mode`);
    } else {
      root.classList.add(`${colorMode}-mode`);
    }
  }, [colorMode]);

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route
            path="/"
            element={
              <RoomCreation colorMode={colorMode} setColorMode={setColorMode} />
            }
          />
          <Route
            path="/room/:roomId"
            element={<Room colorMode={colorMode} setColorMode={setColorMode} />}
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
